import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import type { SwitchPushOptions, SwitchPushPlan } from '../../api/services/ivrDeploy.service';
import { canApply, fieldValue } from '../../api/services/ivrDeploy.service';

/**
 * The switch-push preview: what WOULD change on the switch, field by field, before
 * anything is written (Phase 0 of docs/design/2026-10-14_ivr_app_management.md).
 *
 * Always shown: which switch, whether it has call_request, and the recording folder
 * (business_name) before → after — the field a push used to rewrite silently.
 * Apply stays disabled while the backend reports a blocker.
 */
export interface SwitchPushPreviewProps {
  plan: SwitchPushPlan;
  options: SwitchPushOptions;
  busy: boolean;
  onOptionsChange: (next: SwitchPushOptions) => void;
  /** Re-run the preview with the current options (after choosing a switch or folder). */
  onRefresh: () => void;
  onApply: () => void;
  onCancel: () => void;
}

const actionStyle: Record<string, string> = {
  INSERT: 'bg-green-100 text-green-800',
  UPDATE: 'bg-amber-100 text-amber-800',
  UNCHANGED: 'bg-gray-100 text-gray-600',
};

export default function SwitchPushPreview({
  plan, options, busy, onOptionsChange, onRefresh, onApply, onCancel,
}: SwitchPushPreviewProps) {
  const writes = plan.writes || [];
  const changed = writes.filter(w => w.action !== 'UNCHANGED');
  const bn = plan.businessName;
  const homes = plan.homeProviders || [];
  const folderTyped = (options.businessName || '').trim();

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="switch-push-preview">
      <div className="flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-2">
          <h4 className="font-medium text-blue-900">
            {plan.provider ? `Push to ${plan.provider}` : 'Push to switch'} — nothing written yet
          </h4>
          <p className="text-sm text-blue-800">
            {plan.operatorId} / {plan.serviceId}
            {plan.language ? ` · language ${plan.language}` : ''}
            {plan.statesOnSwitchBefore != null
              ? ` · the switch has ${plan.statesOnSwitchBefore} state(s) in this language`
              : ''}
            {plan.otherLanguagesUntouched?.length
              ? ` · other languages (${plan.otherLanguagesUntouched.join(', ')}) are not touched`
              : ''}
          </p>

          {homes.length > 1 || plan.needsProvider ? (
            <label className="block text-xs text-blue-900">
              Switch
              <select
                className="ml-2 border border-blue-300 rounded px-2 py-1 text-xs bg-white"
                value={options.providerId ?? ''}
                onChange={e => onOptionsChange({ ...options, providerId: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">Choose the switch…</option>
                {homes.map(h => <option key={h.id} value={h.id}>{h.name} (provider {h.id})</option>)}
              </select>
            </label>
          ) : null}

          {plan.callRequest && (
            <p className="text-xs text-blue-900">
              call_request column: <strong>{plan.callRequest.column}</strong>
              {plan.callRequest.sent ? ` — rows written as '${plan.callRequest.sent}'` : ' — not sent'}
              {plan.callRequest.evidence ? <span className="text-blue-700"> ({plan.callRequest.evidence})</span> : null}
            </p>
          )}

          {bn && (
            <div className={`text-xs rounded p-2 ${bn.change ? 'bg-red-50 border border-red-200 text-red-900' : 'bg-white border border-blue-100 text-blue-900'}`}>
              <div>
                Recording folder (business_name): <strong>{fieldValue(bn.before)}</strong> → <strong>{fieldValue(bn.after)}</strong>
                {bn.change ? ' — CHANGES where new recordings are stored and which transfer context is used' : ' (kept)'}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <input
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                  placeholder="keep the switch's folder"
                  value={options.businessName || ''}
                  onChange={e => onOptionsChange({ ...options, businessName: e.target.value, confirmBusinessNameChange: false })}
                  aria-label="Recording folder"
                />
                {folderTyped && (
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={!!options.confirmBusinessNameChange}
                      onChange={e => onOptionsChange({ ...options, confirmBusinessNameChange: e.target.checked })}
                    />
                    I understand this moves the flow's recordings and transfers
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 text-xs">
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Inserts {plan.inserts ?? 0}</span>
            <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded">Updates {plan.updates ?? 0}</span>
            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">Unchanged {plan.unchanged ?? 0}</span>
            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
              Not deleted {plan.pendingRemoval?.length ?? 0}
            </span>
          </div>

          {changed.length > 0 && (
            <details className="text-xs" open={changed.length <= 5}>
              <summary className="cursor-pointer text-blue-900">Field-level changes ({changed.length} state(s))</summary>
              <ul className="mt-1 space-y-1">
                {changed.map(w => (
                  <li key={w.name} className="bg-white border border-blue-100 rounded p-2">
                    <span className={`px-1.5 py-0.5 rounded mr-2 ${actionStyle[w.action]}`}>{w.action}</span>
                    <strong>{w.name}</strong>
                    {w.action === 'UPDATE' && (
                      <table className="mt-1 w-full table-fixed">
                        <tbody>
                          {Object.entries(w.diff).map(([field, d]) => (
                            <tr key={field} className="align-top">
                              <td className="w-28 text-gray-600 pr-2">{field}</td>
                              <td className="break-all text-red-700 pr-2">{fieldValue(d.before)}</td>
                              <td className="break-all text-green-700">{fieldValue(d.after)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {!!plan.pendingRemoval?.length && (
            <p className="text-xs text-amber-800">
              No longer in this flow, left on the switch: {plan.pendingRemoval.map(r => r.name).join(', ')}.
              Removals are applied by the versioned publish (10 minutes after publish, so calls in progress finish).
            </p>
          )}

          {!!plan.warnings?.length && (
            <ul className="text-xs text-amber-800 list-disc list-inside space-y-0.5">
              {plan.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}

          {!!plan.blockers?.length && (
            <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2" role="alert">
              <div className="flex items-center gap-1 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Cannot apply yet</div>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                {plan.blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 pl-8">
        <button
          onClick={onRefresh}
          disabled={busy}
          className="px-4 py-2 border border-blue-300 text-blue-800 text-sm rounded-lg hover:bg-blue-100 disabled:opacity-50"
        >
          Preview again
        </button>
        <button
          onClick={onApply}
          disabled={busy || !canApply(plan)}
          className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50"
          title={canApply(plan) ? 'Write these rows to the switch' : 'Resolve the items above, then preview again'}
        >
          {busy ? 'Writing...' : 'Write to switch'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        {canApply(plan) && changed.length === 0 && (
          <span className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" /> The switch already matches this flow
          </span>
        )}
      </div>
    </div>
  );
}
